// src/components/estimation/detailed/steps/PreparationStep.tsx
import React from 'react';
import { StepContainer } from '../StepContainer';
import { FormSection } from './form/formSection';
import { FormItem } from './form/formItem';

interface PreparationStepProps {
  data: any;
  onChange: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const PreparationStep: React.FC<PreparationStepProps> = ({
  data = {
    diagnostics: {},
    etudes: {},
    autorisations: {},
    gestionProjet: {}
  },
  onChange,
  onNext,
  onBack // This parameter is kept but not used
}) => {
  const updateSection = (section: string, key: string, value: any) => {
    onChange({
      ...data,
      [section]: {
        ...data[section],
        [key]: value
      }
    });
  };

  return (
    <StepContainer
      title="Préparation et études"
      description="Détaillez les études et démarches nécessaires"
      onNext={onNext}
      // Remove onBack prop to disable the back button
    >
      <div className="space-y-6">
        {/* Diagnostics */}
        <FormSection
          title="1. Diagnostics obligatoires"
          description="Diagnostics techniques nécessaires"
        >
          <div className="space-y-4">
            <FormItem
              label="Diagnostic amiante"
              value={data.diagnostics?.amiante || { selected: false }}
              onChange={(value) => updateSection('diagnostics', 'amiante', value)}
              withSurface
            />

            <FormItem
              label="Diagnostic plomb"
              value={data.diagnostics?.plomb || { selected: false }}
              onChange={(value) => updateSection('diagnostics', 'plomb', value)}
              withSurface
            />

            <FormItem
              label="Diagnostic termites en zone à risque"
              value={data.diagnostics?.termiteszonerisque || { selected: false }}
              onChange={(value) => updateSection('diagnostics', 'termiteszonerisque', value)}
            />

            <FormItem
              label="Diagnostic termites hors zone à risque"
              value={data.diagnostics?.termiteshorszone || { selected: false }}
              onChange={(value) => updateSection('diagnostics', 'termiteshorszone', value)}
            />

            <FormItem
              label="Diagnostic DPE"
              value={data.diagnostics?.dpe || { selected: false }}
              onChange={(value) => updateSection('diagnostics', 'dpe', value)}
              withQuantity
            />
          </div>
        </FormSection>

        {/* Études techniques */}
        <FormSection
          title="2. Études techniques"
          description="Études nécessaires pour les travaux"
        >
          <div className="space-y-4">
            <FormItem
              label="Étude structurelle complète"
              value={data.etudes?.structurecomplete || { selected: false }}
              onChange={(value) => updateSection('etudes', 'structurecomplete', value)}
            />

            <FormItem
              label="Étude structurelle partielle"
              value={data.etudes?.structurepartielle || { selected: false }}
              onChange={(value) => updateSection('etudes', 'structurepartielle', value)}
              withSurface
            />

            <FormItem
              label="Étude géotechnique terrain stable"
              value={data.etudes?.geotechniqueterrainstable || { selected: false }}
              onChange={(value) => updateSection('etudes', 'geotechniqueterrainstable', value)}
            />

            <FormItem
              label="Étude géotechnique terrain instable"
              value={data.etudes?.geotechniqueterraininstable || { selected: false }}
              onChange={(value) => updateSection('etudes', 'geotechniqueterraininstable', value)}
            />

            <FormItem
              label="Étude acoustique"
              value={data.etudes?.acoustique || { selected: false }}
              onChange={(value) => updateSection('etudes', 'acoustique', value)}
            />
          </div>
        </FormSection>

        {/* Autorisations */}
        <FormSection
          title="3. Autorisations"
          description="Autorisations administratives requises"
        >
          <div className="space-y-4">
            <FormItem
              label="Permis de construire avec architecte"
              value={data.autorisations?.permisavecarchitecte || { selected: false }}
              onChange={(value) => updateSection('autorisations', 'permisavecarchitecte', value)}
            />

            <FormItem
              label="Permis de construire sans architecte"
              value={data.autorisations?.permissansarchitecte || { selected: false }}
              onChange={(value) => updateSection('autorisations', 'permissansarchitecte', value)}
            />

            <FormItem
              label="Déclaration préalable"
              value={data.autorisations?.declaration || { selected: false }}
              onChange={(value) => updateSection('autorisations', 'declaration', value)}
            />

            <FormItem
              label="Autorisation de la copropriété"
              value={data.autorisations?.copropriete || { selected: false }}
              onChange={(value) => updateSection('autorisations', 'copropriete', value)}
            />
          </div>
        </FormSection>

        {/* Gestion de projet */}
        <FormSection
          title="4. Gestion de projet"
          description="Suivi et coordination du projet"
        >
          <div className="space-y-4">
            <FormItem
              label="Plans d'architecte"
              value={data.gestionProjet?.plans || { selected: false }}
              onChange={(value) => updateSection('gestionProjet', 'plans', value)}
            />

            <FormItem
              label="Suivi par maître d'œuvre"
              value={data.gestionProjet?.suivi || { selected: false }}
              onChange={(value) => updateSection('gestionProjet', 'suivi', value)}
            />
          </div>
        </FormSection>
      </div>
    </StepContainer>
  );
};

export default PreparationStep;
